import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { 
  Search, 
  Filter, 
  X, 
  ChevronDown, 
  Calendar,
  DollarSign,
  Clock,
  AlertTriangle,
  CheckCircle,
  Tag
} from "lucide-react";

export interface TaskFilters {
  search: string;
  dateRange?: {
    from?: Date;
    to?: Date;
  };
  status: string[];
  priority: string[];
  taskType: string[];
  category: string[];
  assignedTo: string;
  caseId: string;
  tags: string[];
  billableOnly: boolean;
  overdueOnly: boolean;
  criticalOnly: boolean;
  milestoneOnly: boolean;
  approvalRequired: boolean;
  completionRange?: {
    min: number;
    max: number;
  };
  sortBy: string;
  sortOrder: 'asc' | 'desc';
}

interface TaskFiltersProps {
  onFiltersChange: (filters: TaskFilters) => void;
  totalTasks: number;
  filteredCount: number;
}

export const TaskFilters = ({ onFiltersChange, totalTasks, filteredCount }: TaskFiltersProps) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [filters, setFilters] = useState<TaskFilters>({
    search: "",
    status: [],
    priority: [],
    taskType: [],
    category: [],
    assignedTo: "",
    caseId: "",
    tags: [],
    billableOnly: false,
    overdueOnly: false,
    criticalOnly: false,
    milestoneOnly: false,
    approvalRequired: false,
    sortBy: "due_date",
    sortOrder: "asc"
  });

  const statusOptions = [
    { value: "Pendente", label: "Pendente", color: "bg-warning" },
    { value: "Em andamento", label: "Em andamento", color: "bg-primary" },
    { value: "Concluída", label: "Concluída", color: "bg-tertiary" },
    { value: "Cancelada", label: "Cancelada", color: "bg-muted" }
  ];

  const priorityOptions = [
    { value: "Alta", label: "Alta", color: "bg-destructive" },
    { value: "Média", label: "Média", color: "bg-warning" },
    { value: "Baixa", label: "Baixa", color: "bg-secondary" }
  ];

  const taskTypeOptions = [
    { value: "geral", label: "Geral" },
    { value: "prazo", label: "Prazo Processual" },
    { value: "reuniao", label: "Reunião" },
    { value: "pesquisa", label: "Pesquisa Jurídica" },
    { value: "audiencia", label: "Audiência" },
    { value: "peticao", label: "Petição" },
    { value: "protocolo", label: "Protocolo" },
    { value: "recurso", label: "Recurso" }
  ];

  const categoryOptions = [
    "Geral", "Cível", "Criminal", "Trabalhista", "Tributário", 
    "Administrativo", "Constitucional", "Empresarial", "Família", "Imobiliário"
  ];

  const sortOptions = [
    { value: "due_date", label: "Data de Vencimento" },
    { value: "created_at", label: "Data de Criação" },
    { value: "priority", label: "Prioridade" },
    { value: "status", label: "Status" },
    { value: "title", label: "Título" },
    { value: "completion_percentage", label: "Progresso" }
  ];

  const updateFilters = (newFilters: Partial<TaskFilters>) => {
    const updated = { ...filters, ...newFilters };
    setFilters(updated);
    onFiltersChange(updated);
  };

  const clearAllFilters = () => {
    const clearedFilters: TaskFilters = {
      search: "",
      status: [],
      priority: [],
      taskType: [],
      category: [],
      assignedTo: "",
      caseId: "",
      tags: [],
      billableOnly: false,
      overdueOnly: false,
      criticalOnly: false,
      milestoneOnly: false,
      approvalRequired: false,
      sortBy: "due_date",
      sortOrder: "asc"
    };
    setFilters(clearedFilters);
    onFiltersChange(clearedFilters);
  };

  const activeFiltersCount = () => {
    let count = 0;
    if (filters.search) count++;
    if (filters.status.length > 0) count++;
    if (filters.priority.length > 0) count++;
    if (filters.taskType.length > 0) count++;
    if (filters.category.length > 0) count++;
    if (filters.assignedTo) count++;
    if (filters.caseId) count++;
    if (filters.tags.length > 0) count++;
    if (filters.billableOnly) count++;
    if (filters.overdueOnly) count++;
    if (filters.criticalOnly) count++;
    if (filters.milestoneOnly) count++;
    if (filters.approvalRequired) count++;
    return count;
  };

  const toggleArrayFilter = (filterKey: keyof TaskFilters, value: string) => {
    const currentArray = filters[filterKey] as string[];
    const newArray = currentArray.includes(value)
      ? currentArray.filter(item => item !== value)
      : [...currentArray, value];
    updateFilters({ [filterKey]: newArray });
  };

  return (
    <div className="space-y-4">
      {/* Quick Search and Results */}
      <div className="flex gap-4 items-center">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Buscar tarefas..."
            value={filters.search}
            onChange={(e) => updateFilters({ search: e.target.value })}
            className="pl-10"
          />
        </div>

        <div className="flex items-center gap-2">
          <Badge variant="outline" className="whitespace-nowrap">
            {filteredCount} de {totalTasks} tarefas
          </Badge>

          <Collapsible open={isExpanded} onOpenChange={setIsExpanded}>
            <CollapsibleTrigger asChild>
              <Button variant="outline" className="flex items-center gap-2">
                <Filter className="w-4 h-4" />
                Filtros
                {activeFiltersCount() > 0 && (
                  <Badge variant="secondary" className="ml-1">
                    {activeFiltersCount()}
                  </Badge>
                )}
                <ChevronDown className={`w-4 h-4 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
              </Button>
            </CollapsibleTrigger>
          </Collapsible>
        </div>
      </div>

      {/* Advanced Filters */}
      <Collapsible open={isExpanded} onOpenChange={setIsExpanded}>
        <CollapsibleContent>
          <Card>
            <CardHeader>
              <div className="flex justify-between items-center">
                <CardTitle className="flex items-center gap-2">
                  <Filter className="w-5 h-5" />
                  Filtros Avançados
                </CardTitle>
                {activeFiltersCount() > 0 && (
                  <Button variant="ghost" size="sm" onClick={clearAllFilters}>
                    <X className="w-4 h-4 mr-2" />
                    Limpar Filtros
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Status and Priority */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <Label className="flex items-center gap-2 mb-3">
                    <CheckCircle className="w-4 h-4" />
                    Status
                  </Label>
                  <div className="flex flex-wrap gap-2">
                    {statusOptions.map((status) => (
                      <Button
                        key={status.value}
                        variant={filters.status.includes(status.value) ? "default" : "outline"}
                        size="sm"
                        onClick={() => toggleArrayFilter('status', status.value)}
                        className="flex items-center gap-2"
                      >
                        <div className={`w-3 h-3 rounded-full ${status.color}`} />
                        {status.label}
                      </Button>
                    ))}
                  </div>
                </div>

                <div>
                  <Label className="flex items-center gap-2 mb-3">
                    <AlertTriangle className="w-4 h-4" />
                    Prioridade
                  </Label>
                  <div className="flex flex-wrap gap-2">
                    {priorityOptions.map((priority) => (
                      <Button
                        key={priority.value}
                        variant={filters.priority.includes(priority.value) ? "default" : "outline"}
                        size="sm"
                        onClick={() => toggleArrayFilter('priority', priority.value)}
                        className="flex items-center gap-2"
                      >
                        <div className={`w-3 h-3 rounded-full ${priority.color}`} />
                        {priority.label}
                      </Button>
                    ))}
                  </div>
                </div>
              </div>

              {/* Task Type and Category */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <Label className="flex items-center gap-2 mb-3">
                    <Tag className="w-4 h-4" />
                    Tipo de Tarefa
                  </Label>
                  <div className="flex flex-wrap gap-2">
                    {taskTypeOptions.map((type) => (
                      <Button
                        key={type.value}
                        variant={filters.taskType.includes(type.value) ? "default" : "outline"}
                        size="sm"
                        onClick={() => toggleArrayFilter('taskType', type.value)}
                      >
                        {type.label}
                      </Button>
                    ))}
                  </div>
                </div>

                <div>
                  <Label className="flex items-center gap-2 mb-3">
                    Categoria
                  </Label>
                  <div className="flex flex-wrap gap-2">
                    {categoryOptions.map((category) => (
                      <Button
                        key={category}
                        variant={filters.category.includes(category.toLowerCase()) ? "default" : "outline"}
                        size="sm"
                        onClick={() => toggleArrayFilter('category', category.toLowerCase())}
                      >
                        {category}
                      </Button>
                    ))}
                  </div>
                </div>
              </div>

              {/* Quick Filters */}
              <div>
                <Label className="mb-3 block">Filtros Rápidos</Label>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                  <div className="flex items-center space-x-2">
                    <Switch
                      id="billableOnly"
                      checked={filters.billableOnly}
                      onCheckedChange={(checked) => updateFilters({ billableOnly: checked })}
                    />
                    <Label htmlFor="billableOnly" className="text-sm flex items-center gap-1">
                      <DollarSign className="w-3 h-3" />
                      Faturáveis
                    </Label>
                  </div>

                  <div className="flex items-center space-x-2">
                    <Switch
                      id="overdueOnly"
                      checked={filters.overdueOnly}
                      onCheckedChange={(checked) => updateFilters({ overdueOnly: checked })}
                    />
                    <Label htmlFor="overdueOnly" className="text-sm flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      Atrasadas
                    </Label>
                  </div>

                  <div className="flex items-center space-x-2">
                    <Switch
                      id="criticalOnly"
                      checked={filters.criticalOnly}
                      onCheckedChange={(checked) => updateFilters({ criticalOnly: checked })}
                    />
                    <Label htmlFor="criticalOnly" className="text-sm flex items-center gap-1">
                      <AlertTriangle className="w-3 h-3" />
                      Críticas
                    </Label>
                  </div>

                  <div className="flex items-center space-x-2">
                    <Switch
                      id="milestoneOnly"
                      checked={filters.milestoneOnly}
                      onCheckedChange={(checked) => updateFilters({ milestoneOnly: checked })}
                    />
                    <Label htmlFor="milestoneOnly" className="text-sm">
                      Marcos
                    </Label>
                  </div>

                  <div className="flex items-center space-x-2">
                    <Switch
                      id="approvalRequired"
                      checked={filters.approvalRequired}
                      onCheckedChange={(checked) => updateFilters({ approvalRequired: checked })}
                    />
                    <Label htmlFor="approvalRequired" className="text-sm">
                      Aprovação
                    </Label>
                  </div>
                </div>
              </div>

              {/* Additional Filters */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="assignedTo">Responsável</Label>
                  <Input
                    id="assignedTo"
                    placeholder="Filtrar por responsável"
                    value={filters.assignedTo}
                    onChange={(e) => updateFilters({ assignedTo: e.target.value })}
                  />
                </div>

                <div>
                  <Label htmlFor="caseId">Processo</Label>
                  <Input
                    id="caseId"
                    placeholder="Filtrar por processo"
                    value={filters.caseId}
                    onChange={(e) => updateFilters({ caseId: e.target.value })}
                  />
                </div>
              </div>

              {/* Sort Options */}
              <div className="flex gap-4 items-end">
                <div className="flex-1">
                  <Label htmlFor="sortBy">Ordenar por</Label>
                  <Select 
                    value={filters.sortBy} 
                    onValueChange={(value) => updateFilters({ sortBy: value })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {sortOptions.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="sortOrder">Ordem</Label>
                  <Select 
                    value={filters.sortOrder} 
                    onValueChange={(value) => updateFilters({ sortOrder: value as 'asc' | 'desc' })}
                  >
                    <SelectTrigger className="w-32">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="asc">Crescente</SelectItem>
                      <SelectItem value="desc">Decrescente</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardContent>
          </Card>
        </CollapsibleContent>
      </Collapsible>
    </div>
  );
};